import { TagProps } from "antd";

export const PALETTE = {
  pastel: [
    { name: "violet", base: { l: 0.55, c: 0.22, h: 285 } },
    { name: "indigo", base: { l: 0.55, c: 0.2, h: 270 } },
    { name: "blue", base: { l: 0.55, c: 0.18, h: 255 } },
    { name: "cyan", base: { l: 0.65, c: 0.14, h: 200 } },
    { name: "teal", base: { l: 0.6, c: 0.14, h: 180 } },
    { name: "emerald", base: { l: 0.6, c: 0.16, h: 160 } },
    { name: "green", base: { l: 0.62, c: 0.18, h: 145 } },
    { name: "lime", base: { l: 0.7, c: 0.2, h: 120 } },
    { name: "yellow", base: { l: 0.8, c: 0.18, h: 95 } },
    { name: "orange", base: { l: 0.65, c: 0.2, h: 60 } },
    { name: "red", base: { l: 0.6, c: 0.22, h: 25 } },
    { name: "pink", base: { l: 0.65, c: 0.2, h: 10 } },
    { name: "fuchsia", base: { l: 0.6, c: 0.24, h: 320 } },
  ],
};

export const hashString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = Math.trunc(hash);
  }
  return Math.abs(hash);
};

export function convertTo(shade: Shades, components: Record<"l" | "c" | "h", number>) {
  if (shade === "dark") {
    components.l = 0.35;
  } else if (shade === "light") {
    components.l = 0.92;
    components.c *= 0.25;
  }

  return `oklch(${components.l} ${components.c} ${components.h})`;
}

type PaletteOptions = keyof typeof PALETTE;
type Color = `${PaletteOptions}.${string}`;
type Shades = "base" | "dark" | "light";

export const pickColor = (selector: Color, shade: Shades = "base") => {
  const [wheel, color] = selector.split(".") as [PaletteOptions, string];
  const selected = PALETTE[wheel].find((val) => val.name === color) ?? PALETTE[wheel][0];
  return convertTo(shade, selected.base);
};

export const pickColorFromSeed = (seed: string, shade: Shades = "base") => {
  const index = hashString(seed) % PALETTE.pastel.length;
  const selected = PALETTE.pastel[index];
  return convertTo(shade, selected.base);
};

type AntdTagColor = NonNullable<TagProps["color"]>;

const ANTD_TAG_COLORS = [
  "magenta",
  "red",
  "volcano",
  "orange",
  "gold",
  "lime",
  "green",
  "cyan",
  "blue",
  "geekblue",
  "purple",
] as const;

export const pickAntdTagColorFromSeed = (seed: string): AntdTagColor => {
  const index = hashString(seed) % ANTD_TAG_COLORS.length;
  return ANTD_TAG_COLORS[index];
};

type TagColorClasses = {
  text: string;
  bg: string;
  border: string;
};

export const TAG_COLOR_TO_TW_CLASS: Record<AntdTagColor, TagColorClasses> = {
  gray: {
    text: "text-gray-500",
    bg: "bg-gray-50",
    border: "border-gray-200",
  },

  pink: {
    text: "text-pink-500",
    bg: "bg-pink-50",
    border: "border-pink-200",
  },

  "pink-inverse": {
    text: "text-white",
    bg: "bg-pink-500",
    border: "border-pink-500",
  },

  red: {
    text: "text-red-500",
    bg: "bg-red-50",
    border: "border-red-200",
  },

  "red-inverse": {
    text: "text-white",
    bg: "bg-red-500",
    border: "border-red-500",
  },

  volcano: {
    text: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
  },

  "volcano-inverse": {
    text: "text-white",
    bg: "bg-red-600",
    border: "border-red-600",
  },

  orange: {
    text: "text-orange-500",
    bg: "bg-orange-50",
    border: "border-orange-200",
  },

  "orange-inverse": {
    text: "text-white",
    bg: "bg-orange-500",
    border: "border-orange-500",
  },

  gold: {
    text: "text-amber-500",
    bg: "bg-amber-50",
    border: "border-amber-200",
  },

  "gold-inverse": {
    text: "text-white",
    bg: "bg-amber-500",
    border: "border-amber-500",
  },

  yellow: {
    text: "text-yellow-600",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
  },

  "yellow-inverse": {
    text: "text-black",
    bg: "bg-yellow-400",
    border: "border-yellow-400",
  },

  lime: {
    text: "text-lime-600",
    bg: "bg-lime-50",
    border: "border-lime-200",
  },

  "lime-inverse": {
    text: "text-black",
    bg: "bg-lime-500",
    border: "border-lime-500",
  },

  green: {
    text: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
  },

  "green-inverse": {
    text: "text-white",
    bg: "bg-green-600",
    border: "border-green-600",
  },

  cyan: {
    text: "text-cyan-500",
    bg: "bg-cyan-50",
    border: "border-cyan-200",
  },

  "cyan-inverse": {
    text: "text-white",
    bg: "bg-cyan-500",
    border: "border-cyan-500",
  },

  blue: {
    text: "text-blue-500",
    bg: "bg-blue-50",
    border: "border-blue-200",
  },

  "blue-inverse": {
    text: "text-white",
    bg: "bg-blue-500",
    border: "border-blue-500",
  },

  geekblue: {
    text: "text-indigo-500",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
  },

  "geekblue-inverse": {
    text: "text-white",
    bg: "bg-indigo-500",
    border: "border-indigo-500",
  },

  purple: {
    text: "text-purple-500",
    bg: "bg-purple-50",
    border: "border-purple-200",
  },

  "purple-inverse": {
    text: "text-white",
    bg: "bg-purple-500",
    border: "border-purple-500",
  },

  magenta: {
    text: "text-fuchsia-500",
    bg: "bg-fuchsia-50",
    border: "border-fuchsia-200",
  },

  "magenta-inverse": {
    text: "text-white",
    bg: "bg-fuchsia-500",
    border: "border-fuchsia-500",
  },

  success: {
    text: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
  },

  processing: {
    text: "text-blue-500",
    bg: "bg-blue-50",
    border: "border-blue-200",
  },

  error: {
    text: "text-red-500",
    bg: "bg-red-50",
    border: "border-red-200",
  },

  warning: {
    text: "text-yellow-600",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
  },

  default: {
    text: "text-gray-500",
    bg: "bg-gray-50",
    border: "border-gray-200",
  },
};
